import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from './AuthContext';
import type { GetInboxConversationsResponse, InboxConversationItem } from '../types/messages';
import {
  applyLocallyViewedUnread,
  hasViewedPrairi31Welcome,
  persistPrairi31WelcomeViewed,
  unreadMessagesBadgeCount,
} from '../utils/inboxUnreadBadge';
import {
  founderUnreadConversations,
  founderUnreadEpisodeFingerprint,
  syncUnreadFounderTipEpisode,
} from '../utils/unreadFounderMessageTip';
import { canSeePrairiThread } from '../utils/internalTester';
import { withoutPrairiTeamConversation } from '../constants/prairiTeamWelcomeMessage';

interface InboxUnreadContextType {
  inboxUnreadCount: number;
  /** True when at least one non-PRAIRI conversation has unread messages. */
  hasFounderUnread: boolean;
  /** Fingerprint for the current founder-unread episode, or null if none. */
  founderUnreadEpisodeKey: string | null;
  welcomeViewed: boolean;
  refreshInboxUnread: () => Promise<void>;
  markPrairi31WelcomeViewed: () => void;
  markConversationViewed: (conversationId: string, lastMessageAt?: string | null) => void;
  setOpenInboxConversation: (conversationId: string | null) => void;
}

const InboxUnreadContext = createContext<InboxUnreadContextType | undefined>(undefined);

export const InboxUnreadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, firebaseUser } = useAuth();
  const userId = user?.id || firebaseUser?.uid || null;
  const isInvestor = user?.userType === 'investor';
  const showPrairiWelcome = isInvestor && canSeePrairiThread(user);
  const canUseInbox = Boolean(
    firebaseUser && (user?.userType === 'investor' || user?.userType === 'startup')
  );

  const [conversations, setConversations] = useState<InboxConversationItem[]>([]);
  const [welcomeViewed, setWelcomeViewed] = useState(false);
  const [viewedById, setViewedById] = useState<Map<string, { lastMessageAt: string | null }>>(
    () => new Map()
  );
  const [openConversationId, setOpenConversationId] = useState<string | null>(null);
  const viewedByIdRef = useRef(viewedById);
  viewedByIdRef.current = viewedById;

  useEffect(() => {
    setWelcomeViewed(hasViewedPrairi31Welcome(userId));
    setConversations([]);
    setViewedById(new Map());
  }, [userId]);

  const refreshInboxUnread = useCallback(async () => {
    if (!canUseInbox) {
      setConversations([]);
      return;
    }
    try {
      const getInbox = httpsCallable<Record<string, never>, GetInboxConversationsResponse>(
        functions,
        'getInboxConversations'
      );
      const result = await getInbox({});
      const loaded = Array.isArray(result.data.conversations) ? result.data.conversations : [];
      const visible = showPrairiWelcome ? loaded : withoutPrairiTeamConversation(loaded);
      setConversations(applyLocallyViewedUnread(visible, viewedByIdRef.current));
      setWelcomeViewed(hasViewedPrairi31Welcome(userId));
    } catch {
      // Keep the last known count (or the local 3.1 welcome 1).
    }
  }, [canUseInbox, showPrairiWelcome, userId]);

  useEffect(() => {
    if (!canUseInbox) return;
    void refreshInboxUnread();
    const refreshIfVisible = () => {
      if (document.visibilityState === 'hidden') return;
      void refreshInboxUnread();
    };
    const intervalId = window.setInterval(refreshIfVisible, 10000);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [canUseInbox, refreshInboxUnread]);

  const markPrairi31WelcomeViewed = useCallback(() => {
    persistPrairi31WelcomeViewed(userId);
    setWelcomeViewed(true);
  }, [userId]);

  const markConversationViewed = useCallback(
    (conversationId: string, lastMessageAt: string | null = null) => {
      if (!conversationId) return;
      setViewedById(prev => {
        const next = new Map(prev);
        next.set(conversationId, { lastMessageAt });
        return next;
      });
      setConversations(prev =>
        prev.map(item =>
          item.conversationId === conversationId ? { ...item, unreadCount: 0 } : item
        )
      );
    },
    []
  );

  const setOpenInboxConversation = useCallback((conversationId: string | null) => {
    setOpenConversationId(conversationId);
  }, []);

  const effectiveConversations = useMemo(
    () => applyLocallyViewedUnread(conversations, viewedById),
    [conversations, viewedById]
  );

  const inboxUnreadCount = useMemo(
    () =>
      canUseInbox
        ? unreadMessagesBadgeCount(effectiveConversations, {
            isInvestor,
            welcomeViewed,
            showPrairiWelcome,
            openConversationId,
          })
        : 0,
    [
      canUseInbox,
      effectiveConversations,
      isInvestor,
      openConversationId,
      showPrairiWelcome,
      welcomeViewed,
    ]
  );

  const founderUnreadEpisodeKey = useMemo(() => {
    if (!canUseInbox || !isInvestor) return null;
    return founderUnreadEpisodeFingerprint(effectiveConversations);
  }, [canUseInbox, effectiveConversations, isInvestor]);

  const hasFounderUnread = useMemo(() => {
    if (!canUseInbox || !isInvestor) return false;
    return founderUnreadConversations(effectiveConversations).length > 0;
  }, [canUseInbox, effectiveConversations, isInvestor]);

  // Count separate browser sessions while a founder-unread episode is open (any page).
  useEffect(() => {
    if (!isInvestor || !userId || !founderUnreadEpisodeKey) return;
    syncUnreadFounderTipEpisode(userId, founderUnreadEpisodeKey);
  }, [founderUnreadEpisodeKey, isInvestor, userId]);

  const value = useMemo(
    () => ({
      inboxUnreadCount,
      hasFounderUnread,
      founderUnreadEpisodeKey,
      welcomeViewed,
      refreshInboxUnread,
      markPrairi31WelcomeViewed,
      markConversationViewed,
      setOpenInboxConversation,
    }),
    [
      inboxUnreadCount,
      hasFounderUnread,
      founderUnreadEpisodeKey,
      welcomeViewed,
      refreshInboxUnread,
      markPrairi31WelcomeViewed,
      markConversationViewed,
      setOpenInboxConversation,
    ]
  );

  return <InboxUnreadContext.Provider value={value}>{children}</InboxUnreadContext.Provider>;
};

export const useInboxUnread = () => {
  const ctx = useContext(InboxUnreadContext);
  if (!ctx) throw new Error('useInboxUnread must be used within InboxUnreadProvider');
  return ctx;
};
